import React from 'react';
import { COBERTURA_REINCLUIR_MESES } from './coberturaSaldo';
import type { FaixaCobertura, FiltrosSaldos, IndicadoresPainel } from './saldosUcs';
import { FAIXA_META } from './saldosUcsVisual';

const Kpi: React.FC<{
  rotulo: string;
  valor: number;
  dica: string;
  ativo: boolean;
  ponto?: string;
  onClick: () => void;
}> = ({ rotulo, valor, dica, ativo, ponto, onClick }) => (
  <button
    onClick={onClick}
    title={dica}
    aria-pressed={ativo}
    className={`text-left rounded-2xl border p-3 shadow-sm transition-colors ${
      ativo
        ? 'bg-primary/10 border-primary/40'
        : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500'
    }`}
  >
    <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
      {ponto ? <span className={`w-2 h-2 rounded-full ${ponto}`} aria-hidden="true" /> : null}
      {rotulo}
    </p>
    <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums mt-1">{valor.toLocaleString('pt-BR')}</p>
  </button>
);

interface KpisSaldosProps {
  indicadores: IndicadoresPainel;
  filtros: FiltrosSaldos;
  setFiltros: (atualizar: (atual: FiltrosSaldos) => FiltrosSaldos) => void;
}

/** Os 8 indicadores de saldo. Contam as UCs da seleção e, clicados, filtram a lista. */
const KpisSaldos: React.FC<KpisSaldosProps> = ({ indicadores, filtros, setFiltros }) => {
  const alternarFaixa = (f: FaixaCobertura | 'sem_medida') =>
    setFiltros(atual => ({ ...atual, status: 'recebendo', divergencia: '', faixa: atual.faixa === f ? null : f }));

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3" role="group" aria-label="Indicadores de saldo">
      <Kpi
        rotulo="Recebendo energia"
        valor={indicadores.recebendo}
        dica="UCs Ativa, 1° Ciclo e 2° Ciclo neste rateio"
        ativo={filtros.status === 'recebendo' && !filtros.faixa && !filtros.divergencia}
        onClick={() => setFiltros(atual => ({ ...atual, status: 'recebendo', faixa: null, divergencia: '' }))}
      />
      {(['falta', 'saudavel', 'atencao', 'excesso'] as FaixaCobertura[]).map(f => (
        <Kpi
          key={f}
          rotulo={FAIXA_META[f].rotulo}
          valor={indicadores[f]}
          dica={FAIXA_META[f].descricao}
          ponto={FAIXA_META[f].ponto}
          ativo={filtros.faixa === f}
          onClick={() => alternarFaixa(f)}
        />
      ))}
      <Kpi
        rotulo="Sem como medir"
        valor={indicadores.semMedida}
        dica="Recebendo energia, mas sem saldo lido na fatura ou sem consumo para comparar"
        ponto="bg-gray-400"
        ativo={filtros.faixa === 'sem_medida'}
        onClick={() => alternarFaixa('sem_medida')}
      />
      <Kpi
        rotulo="Voltar ao rateio"
        valor={indicadores.reincluir}
        dica={`Fora do rateio (Ativa-S/-Rateio) com saldo para menos de ${COBERTURA_REINCLUIR_MESES} meses: peça a volta no próximo envio`}
        ponto="bg-red-500"
        ativo={filtros.divergencia === 'reincluir_no_rateio'}
        onClick={() =>
          setFiltros(atual =>
            atual.divergencia === 'reincluir_no_rateio'
              ? { ...atual, divergencia: '' }
              : { ...atual, faixa: null, status: 'todos', divergencia: 'reincluir_no_rateio', ordenacao: 'cobertura_asc' }
          )
        }
      />
      <Kpi
        rotulo="Com divergência"
        valor={indicadores.comDivergencia}
        dica="Status, geradora, crédito ou cadastro que não batem — inclui UCs que não recebem"
        ponto="bg-amber-500"
        ativo={filtros.divergencia === 'qualquer'}
        onClick={() =>
          setFiltros(atual => ({ ...atual, faixa: null, status: 'todos', divergencia: atual.divergencia === 'qualquer' ? '' : 'qualquer' }))
        }
      />
    </div>
  );
};

export default KpisSaldos;
